import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, Clock } from "lucide-react";
import SEO from "../Components/SEO";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // ✅ Web3Forms API – bina backend form submit + email notification
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formPayload = new FormData();
      formPayload.append("access_key", "d7d1ac35-3d09-4564-9b57-dd9088c9cac9"); // apni key yahan daalein
      formPayload.append("name", formData.name);
      formPayload.append("email", formData.email);
      formPayload.append("phone", formData.phone);
      formPayload.append("subject", formData.subject);
      formPayload.append("message", formData.message);

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formPayload,
      });

      const result = await response.json();

      if (result.success) {
        alert("Message sent successfully!");
        setFormData({
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        alert(result.message || "Failed to send message.");
      }
    } catch (error) {
      console.error(error);
      alert("Something went wrong.");
    }

    setLoading(false);
  };

  const styles = {
    page: {
      minHeight: "100vh",
      background: "linear-gradient(135deg, #f5f7ff 0%, #eef2ff 100%)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      padding: "50px 20px",
      fontFamily: "'Poppins', sans-serif",
    },
    container: {
      width: "100%",
      maxWidth: "1200px",
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: "40px",
      alignItems: "center",
    },
    leftSection: {
      display: "flex",
      flexDirection: "column",
      gap: "20px",
    },
    heading: {
      fontSize: "42px",
      fontWeight: "700",
      color: "#0B2E7A",
      marginBottom: "10px",
    },
    subHeading: {
      color: "#555",
      lineHeight: "1.8",
      fontSize: "16px",
    },
    infoCard: {
      background: "#fff",
      padding: "18px",
      borderRadius: "14px",
      display: "flex",
      alignItems: "center",
      gap: "15px",
      boxShadow: "0 5px 20px rgba(0,0,0,0.08)",
      transition: "0.3s",
    },
    iconBox: {
      width: "55px",
      height: "55px",
      borderRadius: "50%",
      background: "#0B2E7A",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      color: "#fff",
    },
    formCard: {
      background: "#fff",
      padding: "40px",
      borderRadius: "20px",
      boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
    },
    formTitle: {
      fontSize: "28px",
      fontWeight: "700",
      color: "#0B2E7A",
      marginBottom: "25px",
      textAlign: "center",
    },
    inputGroup: {
      marginBottom: "18px",
    },
    input: {
      width: "100%",
      padding: "14px 16px",
      borderRadius: "10px",
      border: "1px solid #ddd",
      outline: "none",
      fontSize: "15px",
      transition: "0.3s",
      boxSizing: "border-box",
    },
    textarea: {
      width: "100%",
      padding: "14px 16px",
      borderRadius: "10px",
      border: "1px solid #ddd",
      outline: "none",
      resize: "none",
      minHeight: "130px",
      fontSize: "15px",
      boxSizing: "border-box",
    },
    button: {
      width: "100%",
      padding: "15px",
      border: "none",
      borderRadius: "10px",
      background: "#0B2E7A",
      color: "#fff",
      fontSize: "16px",
      fontWeight: "600",
      cursor: "pointer",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: "10px",
      transition: "0.3s",
    },
  };

  return (
    <>
      <SEO 
        title="Contact Us | Jaina Enterprises - Scrap Metal Buyers" 
        description="Get in touch with Jaina Enterprises for hassle-free scrap metal recycling. Contact us for bulk deals, industrial scrap disposal, and the latest scrap rates."
        keywords="contact Jaina Enterprises, scrap dealer phone number, sell industrial scrap, scrap rate query"
      />
      {/* 🔧 CSS hover effects (same as before) */}
      <style>
        {`
          @media (max-width: 900px) {
            .contact-container {
              grid-template-columns: 1fr !important;
            }
            .contact-heading {
              font-size: 32px !important;
              text-align: center;
            }
            .contact-left {
              text-align: center;
            }
          }
          .contact-heading {
            position: relative;
            display: inline-block;
            transition: all 0.4s ease;
          }
          .contact-heading::after {
            content: '';
            position: absolute;
            bottom: -8px;
            left: 0;
            width: 0;
            height: 4px;
            background: #FFD700;
            border-radius: 10px;
            transition: 0.4s;
          }
          .contact-heading:hover::after {
            width: 100%;
          }
          .contact-heading:hover {
            color: #1748b5;
          }
          .info-card {
            cursor: pointer;
            transition: all 0.4s ease !important;
          }
          .info-card:hover {
            transform: translateY(-10px);
            background: linear-gradient(135deg, #0B2E7A, #1f56d8);
            box-shadow: 0 20px 40px rgba(11,46,122,0.25);
          }
          .info-card h4,
          .info-card p {
            transition: 0.4s ease;
          }
          .info-card:hover h4,
          .info-card:hover p {
            color: #fff;
          }
          .icon-box {
            transition: all 0.4s ease !important;
          }
          .info-card:hover .icon-box {
            background: #FFD700 !important;
            color: #0B2E7A !important;
            transform: rotate(12deg) scale(1.15);
            box-shadow: 0 10px 25px rgba(255,215,0,0.5);
          }
          .contact-form-card {
            transition: 0.4s ease;
          }
          .contact-form-card:hover {
            transform: translateY(-8px);
            box-shadow: 0 20px 50px rgba(11,46,122,0.15);
          }
          input, textarea {
            transition: 0.3s ease;
          }
          input:hover, textarea:hover {
            border-color: #0B2E7A;
          }
          input:focus, textarea:focus {
            border-color: #0B2E7A;
            box-shadow: 0 0 0 4px rgba(11,46,122,0.12);
            transform: scale(1.02);
          }
          .submit-btn {
            transition: all 0.4s ease !important;
          }
          .submit-btn:hover {
            background: #FFD700 !important;
            color: #0B2E7A !important;
            transform: translateY(-4px);
            box-shadow: 0 12px 25px rgba(255,215,0,0.4);
          }
          .submit-btn:hover svg {
            transform: translateX(5px);
          }
          .submit-btn svg {
            transition: 0.3s ease;
          }
          .info-card a {
            text-decoration: none;
            color: inherit;
            display: flex;
            align-items: center;
            gap: 15px;
            width: 100%;
          }
        `}
      </style>

      <div style={styles.page}>
        <div className="contact-container" style={styles.container}>
          {/* Left Section */}
          <div className="contact-left" style={styles.leftSection}>
            <h1 className="contact-heading" style={styles.heading}>
              Contact Us
            </h1>
            <p style={styles.subHeading}>
              Have questions about scrap buying, selling or recycling services?
              Our team is here to help. Fill out the form and we'll get back to
              you as soon as possible.
            </p>

            {/* 📞 Phone – now clickable */}
            <div className="info-card" style={styles.infoCard}>
              <a href="tel:+918920808492" style={{ display: "flex", alignItems: "center", gap: "15px", flex: 1 }}>
                <div className="icon-box" style={styles.iconBox}>
                  <Phone size={24} />
                </div>
                <div>
                  <h4>Call Us</h4>
                  <p>+91 89208-08492</p>
                </div>
              </a>
            </div>

            {/* ✉️ Email (you can make it mailto: if you want) */}
            <div className="info-card" style={styles.infoCard}>
              <a href="mailto:info@yourscrap.com" style={{ display: "flex", alignItems: "center", gap: "15px", flex: 1 }}>
                <div className="icon-box" style={styles.iconBox}>
                  <Mail size={24} />
                </div>
                <div>
                  <h4>Email Us</h4>
                  <p>info@yourscrap.com</p>
                </div>
              </a>
            </div>

            <div className="info-card" style={styles.infoCard}>
              <div style={{ display: "flex", alignItems: "center", gap: "15px", flex: 1 }}>
                <div className="icon-box" style={styles.iconBox}>
                  <MapPin size={24} />
                </div>
                <div>
                  <h4>Location</h4>
                  <p>Delhi, India</p>
                </div>
              </div>
            </div>

            <div className="info-card" style={styles.infoCard}>
              <div style={{ display: "flex", alignItems: "center", gap: "15px", flex: 1 }}>
                <div className="icon-box" style={styles.iconBox}>
                  <Clock size={24} />
                </div>
                <div>
                  <h4>Working Hours</h4>
                  <p>Mon - Sat : 9:00 AM - 7:00 PM</p>
                </div>
              </div>
            </div>
          </div>

          {/* ✍️ Contact Form – Web3Forms se submit hoga */}
          <div className="contact-form-card" style={styles.formCard}>
            <h2 style={styles.formTitle}>Send Us a Message</h2>

            <form onSubmit={handleSubmit}>
              <div style={styles.inputGroup}>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your Name"
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address"
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone Number"
                  style={styles.input}
                />
              </div>

              <div style={styles.inputGroup}>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Subject"
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.inputGroup}>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Write Your Message..."
                  style={styles.textarea}
                  required
                />
              </div>

              <button
                type="submit"
                className="submit-btn"
                style={styles.button}
                disabled={loading}
              >
                {loading ? "Sending..." : "Send Message"}
                <Send size={18} />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* 🟢 Floating WhatsApp Button – no backend needed */}
      <a
        href={`https://wa.me/918920808492?text=${encodeURIComponent(
          "Hello, I want to know more about your scrap services."
        )}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          position: "fixed",
          bottom: "30px",
          right: "30px",
          backgroundColor: "#25D366",
          color: "#fff",
          padding: "12px 20px",
          borderRadius: "50px",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          textDecoration: "none",
          fontWeight: "600",
          fontSize: "16px",
          boxShadow: "0 4px 15px rgba(37, 211, 102, 0.4)",
          zIndex: 1000,
          transition: "0.3s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.05)")}
        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
      >
        💬 WhatsApp
      </a>

      {/* 📍 Location Section */}
      <section
        style={{
          padding: "80px 20px",
          background: "#f8f9fc",
        }}
      >
        <div
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              fontSize: "36px",
              fontWeight: "700",
              color: "#0B2E7A",
              marginBottom: "15px",
            }}
          >
            Our Location
          </h2>
          <p
            style={{
              color: "#666",
              marginBottom: "40px",
              fontSize: "16px",
            }}
          >
            Visit our office at Mayur Vihar Phase 1, Delhi.
          </p>
          <div
            style={{
              background: "#fff",
              padding: "15px",
              borderRadius: "20px",
              boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
              overflow: "hidden",
            }}
          >
            <iframe
              title="Mayur Vihar Phase 1 Delhi"
              src="https://www.google.com/maps?q=Mayur+Vihar+Phase+1+Delhi&output=embed"
              width="100%"
              height="450"
              style={{
                border: "0",
                borderRadius: "15px",
              }}
              allowFullScreen=""
              loading="lazy"
            ></iframe>
          </div>
        </div>
      </section>
    </>
  );
};

export default Contact;